<template>
  <q-dialog ref="dialogRef" position="bottom" @hide="onDialogHide">
    <q-card class="q-dialog-plugin full-width" :class="{ 'capacitor-safe-area': $q.platform.is.capacitor }">
      <!-- Header with title and close button -->
      <q-card-section class="row items-center q-pa-md">
        <div class="text-h4">Inserat teilen</div>
        <q-space />
        <q-btn v-close-popup icon="close" flat round dense />
      </q-card-section>

      <q-separator />

      <!-- URL Copy Section -->
      <q-card-section class="q-pa-md">
        <div class="row q-gutter-sm items-center">
          <q-input
            :model-value="buildUrl(url, 'linkShare')"
            class="col"
            dense
            label="Inserat URL"
            outlined
            readonly
            @click="$event.target.select()"
          />
          <q-btn
            dense
            flat
            no-caps
            icon="content_copy"
            @click="copy(buildUrl(url, 'linkShare'))"
          >
            Kopieren
          </q-btn>
        </div>
      </q-card-section>

      <q-separator />

      <!-- Social Media Grid -->
      <q-card-section class="q-pa-md">
        <div class="text-subtitle2 q-mb-md">Auf sozialen Medien teilen</div>
        <div class="row q-gutter-md justify-around">
          <div class="col-auto">
            <q-btn
              :href="shareURL.whatsapp"
              class="q-pa-md"
              flat
              round
              size="lg"
              target="_blank"
              @click="onOKClick"
            >
              <q-icon name="fab fa-whatsapp" size="2rem" color="positive" />
            </q-btn>
            <div class="text-center text-caption q-mt-xs">WhatsApp</div>
          </div>

          <div class="col-auto">
            <q-btn
              :href="shareURL.facebook"
              class="q-pa-md"
              flat
              round
              size="lg"
              target="_blank"
              @click="onOKClick"
            >
              <q-icon name="fab fa-facebook" size="2rem" color="blue-7" />
            </q-btn>
            <div class="text-center text-caption q-mt-xs">Facebook</div>
          </div>

          <div class="col-auto">
            <q-btn
              :href="shareURL.twitter"
              class="q-pa-md"
              flat
              round
              size="lg"
              target="_blank"
              @click="onOKClick"
            >
              <q-icon name="fab fa-twitter" size="2rem" color="light-blue-5" />
            </q-btn>
            <div class="text-center text-caption q-mt-xs">Twitter</div>
          </div>

          <div class="col-auto">
            <q-btn
              :href="shareURL.reddit"
              class="q-pa-md"
              flat
              round
              size="lg"
              target="_blank"
              @click="onOKClick"
            >
              <q-icon name="fab fa-reddit" size="2rem" color="deep-orange-6" />
            </q-btn>
            <div class="text-center text-caption q-mt-xs">Reddit</div>
          </div>

          <div class="col-auto">
            <q-btn
              :href="shareURL.linkedin"
              class="q-pa-md"
              flat
              round
              size="lg"
              target="_blank"
              @click="onOKClick"
            >
              <q-icon name="fab fa-linkedin" size="2rem" color="blue-8" />
            </q-btn>
            <div class="text-center text-caption q-mt-xs">LinkedIn</div>
          </div>
        </div>
      </q-card-section>

      <!-- Bottom padding for safe area on mobile -->
      <div class="capacitor-bottom-safe-area"></div>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { copyToClipboard, Notify, useDialogPluginComponent, useQuasar } from 'quasar'
import { reactive, toRefs } from 'vue'

const $q = useQuasar()

const props = defineProps({
  url: {
    required: true
  },
  text: {
    default: 'CamperTrader.de'
  },
  mtmSource: {
    type: String,
    default: undefined
  }
})

defineEmits([
  // REQUIRED; need to specify some events that your
  // component will emit through useDialogPluginComponent()
  ...useDialogPluginComponent.emits
])

const { dialogRef, onDialogHide, onDialogOK } = useDialogPluginComponent()

const buildUrl = function(url, refName = undefined) {
  const matomoParams = refName ? `?mtm_campaign=ShareButtonDetailPage%26mtm_kwd=${refName}&mtm_source=${props.mtmSource}` : ''
  return encodeURI(url) + matomoParams
}

const propsRef = toRefs(props)
const shareUrl = propsRef.url.value

let msg = encodeURIComponent(propsRef.text.value)

const shareURL = reactive({
  facebook: `https://www.facebook.com/share.php?u=${buildUrl(shareUrl, 'facebook')}`,
  twitter: `https://twitter.com/share?url=${buildUrl(shareUrl, 'twitter')}&text=${msg}&hashtags=campertrader,vanlife`,
  linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${buildUrl(shareUrl, 'linkedin')}`,
  reddit: `https://www.reddit.com/submit?url=${buildUrl(shareUrl, 'reddit')}&title=${msg}`,
  whatsapp: `https://api.whatsapp.com/send?text=${msg}: ${buildUrl(shareUrl, 'whatsapp')}`
})

const copy = async function(val) {
  try {
    // For Capacitor apps, we might want to use the native clipboard
    // but copyToClipboard already handles this in Quasar
    await copyToClipboard(val)

    Notify.create({
      message: 'In die Zwischenablage kopiert.',
      color: 'positive',
      icon: 'check',
      position: $q.platform.is.mobile ? 'top' : 'bottom'
    })
  } catch (error) {
    console.warn('Clipboard error:', error)
    Notify.create({
      type: 'negative',
      message: 'Fehler beim kopieren in die Zwischenablage. Bitte kopieren die URL manuell.',
      position: $q.platform.is.mobile ? 'top' : 'bottom'
    })
  }
}

// this is part of our example (so not required)
function onOKClick() {
  // on OK, it is REQUIRED to
  // call onDialogOK (with optional payload)
  onDialogOK()
  // or with payload: onDialogOK({ ... })
  // ...and it will also hide the dialog automatically
}
</script>

<style lang="scss" scoped>
// Capacitor safe area handling
.capacitor-safe-area {
  // Add safe area padding for Capacitor apps
  &.q-card {
    // Ensure the bottom sheet respects safe areas on mobile devices
    padding-bottom: 0; // Reset default padding
  }
}

.capacitor-bottom-safe-area {
  // Safe area padding for bottom (home indicator on iOS, navigation on Android)
  padding-bottom: constant(safe-area-inset-bottom); /* iOS 11.0 */
  padding-bottom: env(safe-area-inset-bottom); /* iOS 11.2+ and Android */
  min-height: 1rem; /* Fallback for devices without safe areas */
}

// Platform-specific adjustments
.desktop .capacitor-bottom-safe-area {
  // No safe area needed on desktop
  padding-bottom: 1rem;
  min-height: 1rem;
}


</style>
